import styled, { keyframes, css } from 'styled-components'
import { Button } from '../../Library/Components/Button'

const enter = keyframes`from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; }`
export const Page = styled.div`
  --surface: ${p => p.theme.colors.background.page};
  --raised: ${p => p.theme.colors.background.primary};
  --tint: ${p => p.theme.colors.background.tertiary};
  --ink: ${p => p.theme.colors.text.primary};
  --muted: ${p => p.theme.colors.text.secondary};
  --line: ${p => p.theme.colors.border.medium};
  --accent: ${p => p.theme.colors.semantic.primary};
  min-height: 100dvh; width: 100%; flex-shrink: 0; background: var(--surface); color: var(--ink);
  font-family: ${p => p.theme.typography.fontFamily}; font-size: 16px; line-height: 1.6;
  * { box-sizing: border-box; }
  h1, h2, h3, p, figure { margin: 0; }
  button, a { -webkit-tap-highlight-color: transparent; }
  a:focus-visible, button:focus-visible, summary:focus-visible { outline: 3px solid var(--accent); outline-offset: 4px; }
  section { scroll-margin-top: 2rem; }
  img { display: block; max-width: 100%; }
  svg { flex-shrink: 0; }
`
export const Header = styled.header`
  max-width: 1400px; margin: auto; height: 64px; padding: 0 3rem;
  display: flex; align-items: center; gap: 1.2rem;
  > button { height: 32px; min-height: 32px; padding: 0 0.8rem; font-size: 0.75rem; }
  > button[aria-label] { width: 32px; padding: 0; }
  > button[aria-label] svg { width: 16px; height: 16px; }
  @media (max-width: 1024px) { padding: 0 1.5rem; gap: 0.75rem; }
  @media (max-width: 767px) {
    height: 68px; padding: 0 1rem; gap: 0.5rem;
    .language { margin-left: auto; }
    > button:last-child { gap: 0; }
    > button:last-child svg { display: none; }
  }
  @media (max-width: 360px) { padding: 0 0.75rem; gap: 0.25rem; > a span { display: none; } }
`
export const Brand = styled.a`
  display: inline-flex; align-items: center; gap: 0.5rem; color: var(--ink); text-decoration: none;
  font-size: 1.25rem; font-weight: 600; letter-spacing: -0.025em;
  img { width: 30px; height: 30px; object-fit: contain; }
`
export const Nav = styled.nav`
  display: flex; gap: 2rem; margin-left: auto; margin-right: 1.5rem;
  a { min-height: 32px; font-size: 0.75rem; }
  @media (max-width: 1024px) { gap: 1rem; margin-right: 0.5rem; }
  @media (max-width: 767px) { display: none; }
`
export const ThemeButton = styled(Button).attrs({ type: 'secondary' })`
  width: 32px; height: 32px; padding: 0; flex-shrink: 0;
  svg { width: 18px; height: 18px; font-size: 18px; }
`
export const AccessButton = styled(Button).attrs({ type: 'primary' })`
  height: 44px; min-height: 44px; padding: 0 1.25rem; flex-shrink: 0;
  background: ${p => p.theme.colors.primary.dark};
  border-color: ${p => p.theme.colors.primary.dark};
  color: ${p => p.theme.name === 'dark' ? p.theme.colors.text.primary : p.theme.colors.text.inverse};
  &:hover:not(:disabled), &:active:not(:disabled) {
    background: ${p => p.theme.colors.primary.dark};
    border-color: ${p => p.theme.colors.primary.dark};
  }
  svg { font-size: 16px; }
  @media (prefers-reduced-motion: reduce) { transition: none; &:hover, &:active { transform: none; } }
  @media (max-width: 767px) { padding: 0 1rem; }
`
export const Hero = styled.section`
  max-width: 1400px; margin: auto; padding: 4.5rem 3rem 5rem;
  display: grid; grid-template-columns: 0.95fr 1.15fr; gap: 0.5rem; align-items: center;
  @media (max-width: 1024px) { padding: 3rem 1.5rem 4rem; grid-template-columns: 1fr 1.1fr; }
  @media (max-width: 767px) { padding: 2rem 1.25rem 2.5rem; grid-template-columns: 1fr; gap: 1.25rem; }
`
export const Intro = styled.div`
  min-width: 0;
  @media (prefers-reduced-motion: no-preference) { animation: ${enter} 500ms ease-out both; }
`
export const Title = styled.h1`
  font-size: clamp(2rem, 3.75vw, 3.5rem); line-height: 1.14; font-weight: 600; letter-spacing: -0.04em;
  span { display: block; color: var(--accent); }
  @media (max-width: 767px) { font-size: clamp(2rem, 7.6vw, 2.6rem); }
`
export const Description = styled.p`
  max-width: 410px; margin-top: 1.4rem !important; font-size: 1rem; line-height: 1.75; color: var(--muted);
`
export const Actions = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 1.6rem; margin-top: 1.75rem;`
export const TextLink = styled.a`
  display: inline-flex; align-items: center; gap: 0.6rem; min-height: 44px; color: var(--ink);
  text-decoration: none; font-size: 0.85rem; font-weight: 500; white-space: nowrap;
  &:hover { color: var(--accent); text-decoration: underline; text-underline-offset: 5px; }
`
export const HeroArtwork = styled.figure`
  min-width: 0; img { width: 100%; height: auto; }
  @media (prefers-reduced-motion: no-preference) { animation: ${enter} 650ms 100ms ease-out both; }
  @media (max-width: 767px) { width: 100%; max-width: 540px; justify-self: center; }
`
export const Modules = styled.section`
  max-width: 1400px; padding: 3rem 3rem 5rem; margin: auto;
  @media (max-width: 1024px) { padding: 2rem 1.5rem 3rem; }
  @media (max-width: 767px) { padding: 2rem 1.25rem 3rem; }
`
export const SectionHeading = styled.div`
  max-width: 650px; margin-bottom: 2.5rem;
  h2 { font-size: clamp(1.7rem, 2.5vw, 2.3rem); font-weight: 600; line-height: 1.2; letter-spacing: -0.045em; }
  p { color: var(--muted); margin-top: 1rem; max-width: 550px; }
`
export const FeatureGrid = styled.div`
  display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 1.5rem; align-items: start;
  @media (max-width: 767px) { grid-template-columns: 1fr; gap: 1.25rem; }
`
export const Feature = styled.article`
  border-radius: ${p => p.theme.radius.card}; overflow: hidden; border: 1px solid var(--line); background: ${p => p.$kind === 'warehouse' ? 'var(--raised)' : 'var(--tint)'};
  ${p => p.$kind === 'documents' && css`margin-top: 4rem;`}
  @media (max-width: 767px) { margin-top: 0; }
`
export const FeatureImage = styled.div`
  padding: 1.5rem 1.5rem 0; img { width: 100%; height: auto; }
  @media (max-width: 767px) { padding: 1rem 1rem 0; }
`
export const FeatureCopy = styled.div`
  padding: 2rem; h3 { font-size: 1.3rem; font-weight: 600; letter-spacing: -0.025em; }
  p { color: var(--muted); margin-top: 0.7rem; max-width: 400px; font-size: 0.95rem; }
  @media (max-width: 767px) { padding: 1.5rem; }
`
export const SupportingModules = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; margin-top: 3rem; padding-top: 2.5rem; border-top: 1px solid var(--line);
  h3 { font-size: 1.1rem; font-weight: 600; } p { color: var(--muted); margin-top: 0.7rem; max-width: 430px; font-size: 0.95rem; }
  @media (max-width: 767px) { grid-template-columns: 1fr; gap: 1.5rem; margin-top: 2rem; padding-top: 2rem; }
`
export const AccessSection = styled.section`
  max-width: 1304px; margin: 0 auto 4rem; padding: 3rem; border-radius: ${p => p.theme.radius.card}; border: 1px solid var(--line); background: var(--raised);
  display: grid; grid-template-columns: 1fr 1fr; gap: 4rem;
  h2 { font-size: clamp(1.7rem, 2.7vw, 2.3rem); line-height: 1.2; font-weight: 600; letter-spacing: -0.045em; }
  p { color: var(--muted); margin-top: 1rem; max-width: 440px; font-size: 0.95rem; }
  button { margin-top: 1.5rem; }
  @media (max-width: 1400px) { margin-left: 3rem; margin-right: 3rem; }
  @media (max-width: 1024px) { margin-left: 1.5rem; margin-right: 1.5rem; padding: 2rem; gap: 2rem; }
  @media (max-width: 767px) { grid-template-columns: 1fr; margin: 0 1.25rem 2rem; padding: 1.5rem; gap: 1.5rem; }
`
export const AccessDetails = styled.div`
  details { border-bottom: 1px solid var(--line); padding: 1rem 0; }
  details:first-child { padding-top: 0; }
  summary { cursor: pointer; font-size: 0.95rem; font-weight: 600; min-height: 32px; }
  p { margin-top: 0.5rem; padding-bottom: 0.3rem; font-size: 0.9rem; }
`
export const Footer = styled.footer`
  max-width: 1400px; margin: auto; padding: 1.5rem 3rem 2rem; display: flex; align-items: center; flex-wrap: wrap; gap: 1.5rem;
  > span { flex: 1; font-size: 0.75rem; color: var(--muted); }
  @media (max-width: 767px) { padding: 1.5rem 1.25rem; gap: 1rem; > span { flex-basis: 100%; order: 3; } }
`
